'use client';

import { Carousel, IconButton } from '@zillow/constellation-10';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { ComponentProps, ReactNode } from 'react';
import { LucideIcon } from '@/src/components/LucideIcon';
import { MIN_MD, MIN_SM } from '@/src/lib/breakpoints';
import * as styles from './listingCarousel.styles';

// Constellation 10.14's `renderNavigationControls` yields button props typed
// as `Partial<IconButtonPropsInterface>`, but JSX-type inference for
// `<IconButton>` narrows to plain `RefAttributes` when a spread is involved.
// 10.17 fixes the inference. Cast to the component's own `ComponentProps` so
// the spread satisfies the narrow inferred type without `@ts-expect-error`.
type IconButtonProps = ComponentProps<typeof IconButton>;

export type ListingCarouselProps = {
  'aria-label': string;
  children: ReactNode;
};

/**
 * `sizes` for `ListingCard`s rendered in the standard fixed-width carousel
 * item (the 280 / 300 / 340 px responsive pattern referenced below). Pass to
 * each card so the browser fetches the Federated Photo variant matching the
 * rendered card instead of the near-viewport-width `ListingCard` default.
 * Composed from the shared breakpoint constants so the thresholds stay in
 * lockstep with the `sm:` / `md:` Panda conditions the item widths use.
 */
export const CAROUSEL_CARD_IMAGE_SIZES = `${MIN_MD} 340px, ${MIN_SM} 300px, 280px`;

const setCarouselTestIds = (element: HTMLElement | null) => {
  const skipLinks = element?.querySelectorAll('[data-c11n-component="Carousel.SkipLink"]');
  skipLinks?.[0]?.setAttribute('data-testid', 'link-skip-to-the-end-of-the-carousel');
  skipLinks?.[1]?.setAttribute('data-testid', 'link-skip-to-the-beginning-of-the-carousel');
};

/**
 * ListingCarousel — horizontal scroll rail for `ListingCard` children
 * (or, during loading states, Constellation `<PropertyCard loading />`
 * skeletons; the wrapper itself is card-type-agnostic).
 *
 * Wraps Constellation's `<Carousel>` with three HotPads conventions:
 *   1. Fixed `slideGap="md"` and `slideAlign="start"` — every listing rail
 *      across the site shares the same rhythm.
 *   2. Replaces the in-header nav controls with absolutely-positioned
 *      circular Lucide chevron buttons that sit inside the carousel's left
 *      and right edges, vertically centered.
 *   3. When a direction has no more slides to scroll to, the corresponding
 *      chevron is hidden entirely rather than rendered in a disabled/greyed
 *      state. The buttons are absolutely positioned, so omitting them does
 *      not shift the rail's layout.
 *
 * Pass each card as a direct child — Constellation auto-wraps each in a
 * `<CarouselSlide>`, so no manual slide wrapper is needed. Consumers
 * typically box each card in a fixed-width div to lock the rail rhythm
 * (see `BudgetExplorer` / `FeaturedRentals` for the 280 / 300 / 340 px
 * responsive pattern).
 */
export function ListingCarousel({ children, ...rest }: ListingCarouselProps) {
  return (
    <Carousel
      ref={setCarouselTestIds}
      slideGap="md"
      slideAlign="start"
      css={{ position: 'relative' }}
      renderNavigationControls={(leftButtonProps, rightButtonProps) => (
        <>
          {!leftButtonProps.disabled && (
            <IconButton
              {...(leftButtonProps as IconButtonProps)}
              shape="circle"
              tone="neutral"
              emphasis="filled"
              size="sm"
              title="Previous"
              className={styles.prevButton}
            >
              <LucideIcon icon={ChevronLeft} />
            </IconButton>
          )}
          {!rightButtonProps.disabled && (
            <IconButton
              {...(rightButtonProps as IconButtonProps)}
              shape="circle"
              tone="neutral"
              emphasis="filled"
              size="sm"
              title="Next"
              className={styles.nextButton}
              data-testid="button-next"
            >
              <LucideIcon icon={ChevronRight} />
            </IconButton>
          )}
        </>
      )}
      {...rest}
    >
      {children}
    </Carousel>
  );
}
